import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'

import {CartContext} from '../../contexts/CartContextProvider'
import FamilyMember from '../../dtos/FamilyMember'
import Offer from '../../dtos/Offer'
import User from '../../dtos/User'
import {IOfferOption} from '../../dtos/Wish'
import useMediaQuery from '../../hooks/useMediaQuery'
import {getHtmlContent} from '../../utils/text'
import {IActivity, ICalendarGroup} from '../../views/calendar/types'
import GroupInformationCollapseView from '../../views/collapseView/components/GroupInformationCollapseView'
import OfferItem from '../../views/memberships/OfferItem'
import {AddToCartResponse, getOffersForGroup} from '../requests'
import {Button, OutlineButton, PrimaryButton, PrimaryLinkButton} from '../styled-components/buttons'
import AddedToCartBanner, {AddedToCartBannerProps} from './AddedToCartBanner'
import InputSelect from './forms/InputSelect'
import {PageContentEnd} from './PageContentEnd'
import RestrictionMessage from './RestrictionMessage'
import {ConsentModal} from "./ConsentModal";
import {ApplicationFlowContext} from "../../contexts/ApplicationFlowContextProvider";
import {IRestrictions} from "../../views/wishlist/types";

const ActivityImage = styled.img`
    flex-shrink: 0;
    float: left;
    margin-right: 30px;
    margin-bottom: 15px;
    display: inline-block;
    height: 200px;
    width: 200px;

    @media (max-width: 767px) {
        display: none;
    }
`

const ActivityImageMobile = styled.img`
    margin-right: 8px;
    height: 36px;
    width: 36px;
    @media (min-width: 768px) {
        display: none;
    }
`

const ActivityDetails = styled.div`
    text-align: justify;
`

const ActivityDescription = styled.div`
    @media (max-width: 767px) {
        font-size: 0.85rem;
    }
`

const ActivityDescriptionWrapper = styled.div<{
    hasOverflow: boolean,
    isExpanded: boolean
}>`
    position: relative;
    ${({hasOverflow}) => hasOverflow
            ? `
      &::after {
        content: "";
        display: block;
        position: absolute;
        bottom: 0;
        height: 60px;
        width: 100%;
        background: linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 30%, rgba(255,255,255,0) 100%);
      }
    `
            : ''
    }
    ${({isExpanded}) => isExpanded
            ? 'padding-bottom: 25px;'
            : `
    @media (max-width: 767px) {
      max-height: 200px;
      overflow: hidden;
    }
    `
    }
`

const ReadMore = styled.button`
    position: absolute;
    z-index: 1;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    background: none;
    border: none;
    color: var(--blue);
`

const StyledInputSelect = styled(InputSelect)`
    width: 300px;
`

const InputsContainer = styled.div`
    margin-top: 20px;
`

const ContinueShoppingButton = styled(Button)`
    background-color: #aaa;
    margin-right: 20px;

    &:hover {
        background-color: #666;
    }
`

const RemoveFromCartButton = styled(OutlineButton)`
    margin-right: 20px;
`;

const GroupDescriptionContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    column-gap: 20px;
`

const ErrorsContainer = styled.div`
    margin-top: 15px;
    color: red;
`

const ButtonWithRestrictionsContainer = styled.div`
    ${PrimaryButton} {
        margin-left: auto;
    }
`

const ShoppingButtons = styled.div`
    display: flex;

    button {
        font-size: clamp(9px, 2.8vw, 14px);
    }
`

interface IButtonsProps {
    goToLogin: () => void
    group: ICalendarGroup,
    onContinueShopping?: () => void
    selectedOffer: Offer | undefined
    selectedOfferOption: IOfferOption | undefined | null
    selectedPlanId: number
    shopUrl: string | undefined
    user: Partial<User> | null
    refreshOffers: () => Promise<void>
    isLoading: boolean
    setIsLoading: (isLoading: boolean) => void
    toggleShoppingButtonDisability: (val: boolean) => void
    isShoppingButtonDisabled: boolean
    setIsAddedToWaitingList: (val: boolean) => void
    isAddedToWaitingList: boolean,
    setSelectedOfferOption: (val: IOfferOption | undefined) => void
    setCartAddCount: (val: (prev: number) => number) => void
    setHasAddedToCart: (val: boolean) => void
    familyMembers: Record<string, Partial<FamilyMember>> | undefined
    setLastAdded: (info: AddedToCartBannerProps | undefined) => void
}

const Buttons: React.FC<IButtonsProps> = ({
                                              selectedOfferOption,
                                              onContinueShopping,
                                              shopUrl,
                                              group,
                                              user,
                                              selectedPlanId,
                                              selectedOffer,
                                              goToLogin,
                                              refreshOffers,
                                              isLoading,
                                              setIsLoading,
                                              toggleShoppingButtonDisability,
                                              isShoppingButtonDisabled,
                                              setIsAddedToWaitingList,
                                              isAddedToWaitingList,
                                              setSelectedOfferOption,
                                              setCartAddCount,
                                              setHasAddedToCart,
                                              familyMembers,
                                              setLastAdded
                                          }) => {
    const {t} = useTranslation();
    const {addItem, deleteItem, isAddedToCart} = useContext(CartContext);
    const {organizationId} = useContext(ApplicationFlowContext);
    const [restrictions, setRestrictions] = useState<AddToCartResponse['restrictions']>([]);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [hasConsent, setHasConsent] = useState<boolean>(false);

    const onConsent = () => {
        setIsModalOpen(false);
        setHasConsent(true);
    }

    const onAdd = useCallback(
        async () => {
            if (!selectedOfferOption) {
                return
            }
            setIsLoading(true)
            setRestrictions([])
            const response = await addItem(selectedOfferOption.subscriber, selectedPlanId)
            await refreshOffers();
            setRestrictions(response.restrictions || [])

            if (response.status === 'OK') {
                const participant = familyMembers && familyMembers[String(selectedOfferOption.subscriber)]
                setLastAdded({
                    participantName: participant && participant.full_name ? participant.full_name : '',
                    planName: selectedOffer ? selectedOffer.plan.name : '',
                    price: selectedOfferOption.price,
                    tax: selectedOfferOption.tax_price,
                    isTaxIncluded: selectedOfferOption.is_tax_included,
                })
                setSelectedOfferOption(undefined)
                setCartAddCount((prev: number) => prev + 1)
                setHasAddedToCart(true)
            }

            setIsLoading(false)
        },
        [addItem, selectedOfferOption, selectedOffer, familyMembers, selectedPlanId, setRestrictions, setSelectedOfferOption, setCartAddCount],
    )

    const onSubscribeToWaitingList = useCallback(
        async () => {
            if (!selectedOfferOption) {
                return;
            }

            setIsLoading(true);
            await addItem(selectedOfferOption.subscriber, selectedPlanId, 'offers.successfullyAddedToWaitingList');
            await refreshOffers();

            setSelectedOfferOption(undefined);
            setCartAddCount((prev: number) => prev + 1);
            setHasAddedToCart(true);
            setIsLoading(false);
        },
        [addItem, selectedOfferOption, selectedPlanId, setSelectedOfferOption, setCartAddCount],
    )

    const removeAddedItemFromCart = useCallback(
        async () => {
            if (!selectedOfferOption) {
                return
            }
            setIsLoading(true)
            await deleteItem(selectedOfferOption.subscriber, selectedPlanId)
            await refreshOffers()
            setIsLoading(false)
            deleteItem(selectedOfferOption.subscriber, selectedPlanId)
            setSelectedOfferOption(undefined);
            setCartAddCount((prev: number) => prev + 1);
            setLastAdded(undefined);
        },
        [deleteItem, selectedOfferOption, selectedPlanId, setSelectedOfferOption, setCartAddCount, setLastAdded],
    )

    const makeListOfRestrictionCodes = (restrictions: IRestrictions) => {
        let groupRestrictions: never[] = [];
        restrictions.groups_restrictions.map((groupRestriction) => groupRestriction.restrictions)
            .forEach((restrictions) => {
                groupRestrictions = groupRestrictions.concat(restrictions.filter((restriction: any) => {
                    return groupRestrictions.find(
                        (addedRestriction) => addedRestriction === restriction
                    ) === undefined;
                }));
            });

        const productMembershipRestrictions = restrictions.product_restrictions.filter((restriction) => {
            return restriction.memberships !== undefined;
        })

        const productRestrictions = restrictions.product_restrictions.filter((restriction) => {
            return restriction.memberships === undefined;
        }).map((restriction) => {
            return restriction.code
        });


        return ([] as (any | never)[]).concat(
            restrictions.organization_restrictions,
            restrictions.registration_restrictions,
            restrictions.membership_restrictions,
            productRestrictions,
            groupRestrictions,
            productMembershipRestrictions
        );
    }

    const restrictionFlatten = restrictions && restrictions.length > 0 ? makeListOfRestrictionCodes(restrictions as unknown as IRestrictions) : [];
    const selectedOfferRestrictions = selectedOfferOption ? makeListOfRestrictionCodes(selectedOfferOption.restrictions) : [];

    const hasAddToCartDisabled = useMemo(() => {
        if (!user || !selectedOfferOption || !selectedOffer) {
            return true
        }
        return selectedOfferRestrictions.some(x => {
            if (group.waiting_list) {
                return x !== 'GROUP_IS_FULL'
            }
            return x
        })
    }, [user, selectedOfferOption, selectedOffer, group, selectedOfferRestrictions])

    if (selectedOfferOption) {
        if (isAddedToCart(selectedOfferOption.subscriber, selectedPlanId)) {
            return (
                <ShoppingButtons>
                    {
                        onContinueShopping &&
                        <ContinueShoppingButton
                            className={"btn btn-close" + (isShoppingButtonDisabled ? " disabled" : "")}
                            onClick={onContinueShopping}
                            disabled={isShoppingButtonDisabled}
                        >
                            {t('wishlist.continueShopping')}
                        </ContinueShoppingButton>
                    }
                    <RemoveFromCartButton
                        className={"btn btn-remove-from-cart"}
                        onClick={removeAddedItemFromCart}
                    >{t('offers.removeFromCart')}</RemoveFromCartButton>
                    {
                        shopUrl &&
                        <PrimaryLinkButton
                            className={"btn btn-go-to-cart"}
                            href={shopUrl} target="_top"
                        >
                            {t('offers.consultCart')}
                        </PrimaryLinkButton>
                    }
                </ShoppingButtons>
            )
        }
        if (selectedOfferOption.on_waiting_list) {
            return (
                <ShoppingButtons>
                    {
                        onContinueShopping &&
                        <ContinueShoppingButton
                            className={"btn btn-close" + (isShoppingButtonDisabled ? " disabled" : "")}
                            onClick={onContinueShopping}
                            disabled={isShoppingButtonDisabled}>
                            {t('wishlist.continueShopping')}
                        </ContinueShoppingButton>
                    }
                    <Button
                        className={"btn btn-add-to-waiting-list disabled"}
                        disabled
                    >
                        {t('offers.subscribedToTheWaitingList')}
                    </Button>
                    {
                        shopUrl &&
                        <PrimaryLinkButton
                            className={"btn btn-go-to-cart"}
                            href={shopUrl}
                            target="_top"
                        >
                            {t('offers.consultCart')}
                        </PrimaryLinkButton>
                    }
                </ShoppingButtons>
            )
        }
    }

    const hasWaitingListAvailable = group.waiting_list
        && group.nbr_places_remaining !== null
        && group.nbr_places_remaining <= 0;

    if (!user) {
        return (
            <ShoppingButtons>
                <ContinueShoppingButton
                    className={"btn btn-close"}
                    onClick={onContinueShopping}
                >
                    {t('wishlist.continueShopping')}
                </ContinueShoppingButton>
                <PrimaryButton
                    className={"btn" + (hasWaitingListAvailable ? ' btn-add-to-waiting-list' : ' btn-add-to-cart') + (hasAddToCartDisabled || isLoading ? " disabled" : "")}
                    onClick={goToLogin}
                    disabled={hasAddToCartDisabled || isLoading}
                >
                    {hasWaitingListAvailable ? t('offers.addToWaitingList') : t('offers.addToCart')}
                </PrimaryButton>
            </ShoppingButtons>
        )
    }

    const finalRestrictions: AddToCartResponse['restrictions'] = restrictionFlatten.filter(x => {
        if (group.waiting_list) {
            return x !== 'GROUP_IS_FULL'
        }
        return x
    }) || [];

    const hasContact = undefined !== user.contacts
        && undefined !== user.contacts.find((contact) => contact.organization_id === organizationId)

    const onClickAction = hasWaitingListAvailable ? onSubscribeToWaitingList : onAdd;

    return (
        <ButtonWithRestrictionsContainer>
            {
                user.id && organizationId && !(hasConsent || hasContact)
                && <ConsentModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onConfirm={onClickAction}
                    onConsent={onConsent}
                    organizationId={organizationId}
                    userId={user.id}
                />
            }
            <ShoppingButtons>
                {
                    onContinueShopping &&
                    <ContinueShoppingButton
                        className={"btn btn-close" + (isShoppingButtonDisabled ? " disabled" : "")}
                        onClick={onContinueShopping}
                        disabled={isShoppingButtonDisabled}
                    >
                        {t('wishlist.continueShopping')}
                    </ContinueShoppingButton>
                }
                <PrimaryButton
                    className={"btn" + (hasWaitingListAvailable ? ' btn-add-to-waiting-list' : ' btn-add-to-cart') + (isAddedToWaitingList || hasAddToCartDisabled || isLoading ? " disabled" : "")}
                    onClick={(hasConsent || hasContact) ? onClickAction : () => setIsModalOpen(true)}
                    disabled={isAddedToWaitingList || hasAddToCartDisabled || isLoading}
                >
                    {hasWaitingListAvailable ? t('offers.addToWaitingList') : t('offers.addToCart')}
                </PrimaryButton>
            </ShoppingButtons>
            {
                finalRestrictions.length > 0 &&
                <RestrictionMessage
                    restrictions={finalRestrictions}
                    multipleGroups={Number(selectedOffer?.plan.groups.length) > 1}
                    group={group}
                    renderContainer={messages => {
                        return (
                            <ErrorsContainer>
                                {
                                    messages.map(x => <div>{x}</div>)
                                }
                            </ErrorsContainer>
                        )
                    }}
                    toggleShoppingButtonDisability={toggleShoppingButtonDisability}
                />
            }
        </ButtonWithRestrictionsContainer>
    )
}

const MAX_TITLE_LENGTH_ON_MOBILE = 45

interface IGroupOffersProps {
    user: Partial<User> | null
    goToLogin: () => void
    activity: IActivity,
    group: ICalendarGroup,
    shopUrl: string | undefined
    closeButton?: boolean
    onContinueShopping?: () => void
    organizationSlug: string | undefined
    shouldRedirectToLogin?: boolean
}

export const GroupOffers: React.FC<IGroupOffersProps> = ({
                                                             activity,
                                                             group,
                                                             shopUrl,
                                                             user,
                                                             goToLogin,
                                                             closeButton = true,
                                                             onContinueShopping,
                                                             organizationSlug,
                                                             shouldRedirectToLogin = true
                                                         }) => {
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(false)
    const [offers, setOffers] = useState<Offer[] | undefined>(undefined)
    const [hasSessionOffer, setHasSessionOffer] = useState<boolean>(false);
    const [hasDropInOffer, setHasDropInOffer] = useState<boolean>(false);
    const [familyMembers, setFamilyMembers] = useState<Record<string, Partial<FamilyMember>> | undefined>(undefined)
    const [selectedOfferOption, setSelectedOfferOption] = useState<IOfferOption | undefined>(undefined)
    const [selectedPlanId, setSelectedPlanId] = useState(-1)
    const [isShoppingButtonDisabled, setIsShoppingButtonDisabled] = useState(false)
    const [isAddedToWaitingList, setIsAddedToWaitingList] = useState(false)
    const [cartAddCount, setCartAddCount] = useState(0);
    const [hasAddedToCart, setHasAddedToCart] = useState(false);
    const [lastAdded, setLastAdded] = useState<AddedToCartBannerProps | undefined>(undefined);

    const refreshOffers = useCallback(async () => {
        return await getOffersForGroup(group.id)
            .then(({offers, familyMembers}) => {
                setOffers(offers)
                setFamilyMembers(familyMembers)
                if (offers.length === 1) {
                    setSelectedPlanId(offers[0].plan.id)
                }

                setHasSessionOffer(offers.find((offer) => offer.plan.type_plan === "SESSION") !== undefined)
                setHasDropInOffer(offers.find((offer) => offer.plan.type_plan === "PASS") !== undefined)
            })
    }, [group, setOffers, setFamilyMembers])

    useEffect(() => {
        if (!user && shouldRedirectToLogin) {
            goToLogin()
        }
        refreshOffers()
    }, [refreshOffers, goToLogin, shouldRedirectToLogin])

    const selectedOffer = useMemo(() => {
        return offers && offers.find(x => x.plan.id === selectedPlanId)
    }, [offers, selectedPlanId])

    const toggleShoppingButtonDisability = (val: boolean) => {
        setIsShoppingButtonDisabled(val)
    }

    const isMobile = useMediaQuery('(max-width: 767px)');

    const header = (
        // NOTE: fix for the compilation error according to https://www.gitmemory.com/issue/react-bootstrap/react-bootstrap/5449/700591508
        // @ts-ignore
        <Modal.Header closeButton={closeButton}>
            <ActivityImageMobile src={activity.mediaPath}/>
            <Modal.Title className={"title"}>
                {
                    isMobile && activity.name.length > MAX_TITLE_LENGTH_ON_MOBILE
                        ? activity.name.slice(0, MAX_TITLE_LENGTH_ON_MOBILE) + '...'
                        : activity.name
                }
            </Modal.Title>
        </Modal.Header>
    )

    const [isVisible, setIsVisible] = useState<boolean | undefined>(false)
    const [isExpanded, setIsExpanded] = useState(false)

    return (
        <>
            {header}
            <Modal.Body>
                <ActivityDetails>
                    <ActivityImage className={"thumbnail"} src={activity.mediaPath}/>
                    <ActivityDetails>
                        <ActivityDescriptionWrapper hasOverflow={!isVisible} isExpanded={isExpanded}>
                            <ActivityDescription
                                className={"description"}
                                dangerouslySetInnerHTML={{__html: getHtmlContent(group.description) || group.activity.description}}
                            />
                            <PageContentEnd onIsVisibleChange={setIsVisible}/>
                            {
                                (!isVisible || isExpanded) &&
                                <ReadMore onClick={() => setIsExpanded(!isExpanded)}>
                                    {
                                        isExpanded
                                            ? t('general.less')
                                            : t('general.readMore')
                                    }
                                </ReadMore>
                            }
                        </ActivityDescriptionWrapper>
                        <GroupDescriptionContainer>
                            <GroupInformationCollapseView
                                group={group}
                                withLocation
                                hasDropInOffer={hasDropInOffer}
                                hasSessionOffer={hasSessionOffer}
                            />
                        </GroupDescriptionContainer>
                    </ActivityDetails>
                </ActivityDetails>
                <InputsContainer>
                    {
                        !offers &&
                        <div>{t('offers.loading')}</div>
                    }
                    {
                        offers && offers.length > 1 &&
                        <StyledInputSelect
                            value={selectedOffer && {
                                label: selectedOffer.plan.name,
                                value: selectedOffer.plan.id
                            }}
                            options={offers.map(x => ({label: x.plan.name, value: x.plan.id}))}
                            onChange={({value}: { value: number }) => setSelectedPlanId(value)}
                            placeholder={t('wishlist.none')}
                            isSearchable={false}
                            withCaret={true}
                        />
                    }
                    {
                        familyMembers && selectedOffer && (
                            <OfferItem
                                key={cartAddCount}
                                isAddedToWaitingList={isAddedToWaitingList}
                                shouldRenderMembershipOptions
                                offer={selectedOffer}
                                activityId={activity.id}
                                organizationSlug={organizationSlug}
                                familyMembers={familyMembers}
                                onMemberSelected={setSelectedOfferOption}
                                refreshOptions={() => {
                                    return getOffersForGroup(group.id)
                                        .then(({offers, familyMembers}) => {
                                            setOffers(offers)
                                            setFamilyMembers(familyMembers)

                                            if (selectedOfferOption) {
                                                const selectedOffer = offers.find((offer) => offer.plan.id === selectedPlanId)
                                                const refreshedSelectedOfferOption = selectedOffer
                                                    ? selectedOffer.options.find((option) => option.subscriber === selectedOfferOption.subscriber)
                                                    : selectedOfferOption;

                                                setSelectedOfferOption(refreshedSelectedOfferOption);
                                            }
                                        })
                                }}
                                toggleShoppingButtonDisability={toggleShoppingButtonDisability}
                            />
                        )
                    }
                    {
                        !user && (
                            <PrimaryButton
                                className={"btn btn-login"}
                                onClick={goToLogin}
                            >
                                {t('wishlist.goToLogin')}
                            </PrimaryButton>
                        )
                    }
                </InputsContainer>
                {
                    lastAdded &&
                    <AddedToCartBanner {...lastAdded} />
                }
            </Modal.Body>
            <Modal.Footer>
                <Buttons
                    isAddedToWaitingList={isAddedToWaitingList}
                    setIsAddedToWaitingList={setIsAddedToWaitingList}
                    selectedPlanId={selectedPlanId}
                    selectedOfferOption={selectedOfferOption}
                    user={user}
                    group={group}
                    shopUrl={shopUrl}
                    onContinueShopping={onContinueShopping}
                    selectedOffer={selectedOffer}
                    goToLogin={goToLogin}
                    refreshOffers={refreshOffers}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    toggleShoppingButtonDisability={toggleShoppingButtonDisability}
                    isShoppingButtonDisabled={isShoppingButtonDisabled}
                    setSelectedOfferOption={setSelectedOfferOption}
                    setCartAddCount={setCartAddCount}
                    setHasAddedToCart={setHasAddedToCart}
                    familyMembers={familyMembers}
                    setLastAdded={setLastAdded}
                />
                {
                    hasAddedToCart && shopUrl && !selectedOfferOption &&
                    <ShoppingButtons>
                        <PrimaryLinkButton
                            className={"btn btn-go-to-cart"}
                            href={shopUrl}
                            target="_top"
                        >
                            {t('offers.consultCart')}
                        </PrimaryLinkButton>
                    </ShoppingButtons>
                }
            </Modal.Footer>
        </>
    )
}
